import { forwardRef, useImperativeHandle, useRef } from 'react';
import useModalState from '@sdk/components/ui/modal/useModalState';
import Modal from '@sdk/components/ui/modal/Modal';
import H3 from '@sdk/components/ui/typography/H3';
import Button from '@sdk/components/ui/buttons/Button';
import ProgressButton from '@sdk/components/ui/buttons/ProgressButton';
import gettext from '@sdk/lang/gettext';
import ModalErrorDisplay from '@sdk/components/ui/modal/components/modalErrorDisplay/ModalErrorDisplay';
import OverlayGuard from '@sdk/components/OverlayGuard/OverlayGuard';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import { Placement } from '@sdk/components/OverlayGuard/OverlayGuard.types';
import { INPUT_HEIGHT } from '@sdk/components/forms/constants';
import type { OrgSettings } from '@sdk/authentication/types/OrganisationType';
import type { PersonasDict } from '@sdk/personaCollection/types/PersonasDict';

import type { UpdateOrgSettings } from '../onboardingChecklist.types';
import {
  Container,
  CenterContentRow,
  ModalContent,
  FooterRow,
  ButtonContainer,
} from '../components/modalBase.style';

import TimeTracking from './TimeTracking';
import Geofencing from './Geofencing';
import EarlyClockIn from './EarlyClockIn';
import AutomaticClockOut from './AutomaticClockOut';
import { useTimeclockForm } from './hooks/useTimeclockForm';

type ForwardedAttrs = {
  open: () => void;
};

type Props = {
  orgSettings: OrgSettings;
  personasDict: PersonasDict;
  timeclockSaveCallback: () => void;
  hasLocationsWithoutAddress: boolean;
  updateOrgSettings: UpdateOrgSettings;
};

const TimeClockModal = forwardRef<ForwardedAttrs, Props>(
  (
    {
      orgSettings,
      personasDict,
      timeclockSaveCallback,
      hasLocationsWithoutAddress,
      updateOrgSettings,
    },
    ref
  ) => {
    const { isVisible, open, close } = useModalState();
    const config = useRef({ noDismiss: true }).current;
    const { timeClockFields, submit, isValid, reset, submitErrors } = useTimeclockForm({
      orgSettings,
      personasDict,
      close,
      updateOrgSettings,
    });

    useImperativeHandle(ref, () => ({
      open,
    }));

    const {
      clockInEarlyEnabled,
      clockInGeoRequired,
      imperialUnits,
      clockInMaxDistance,
      clockInEarlyMinutes,
      clockInFromWebAndApps,
      clockInEnabled,
      clockInAutoClockoutEnabled,
      clockInAutoClockoutMinutes,
    } = timeClockFields;

    const tooltipContent = gettext(
      'Enable the time tracking feature first, to be able to adjust this setting'
    );

    const submitHandler = async () => {
      await submit();
      if (
        hasLocationsWithoutAddress &&
        clockInGeoRequired.value &&
        clockInFromWebAndApps.value &&
        clockInEnabled.value
      ) {
        timeclockSaveCallback();
      }
    };

    if (!isVisible) {
      return null;
    }

    return (
      <Modal fixed config={config} isVisible={isVisible} onDismiss={close}>
        <Container>
          <CenterContentRow pTop={20} pBottom={20}>
            <H3>{gettext('Enable time clock')}</H3>
          </CenterContentRow>
          <ModalContent pLeft={15} pRight={15}>
            <TimeTracking
              clockInEnabled={clockInEnabled}
              clockInFromWebAndApps={clockInFromWebAndApps}
            />

            <OverlayGuard
              hasAccess={Boolean(clockInEnabled.value && clockInFromWebAndApps.value)}
              tooltipContent={tooltipContent}
              placement={Placement.right}>
              <Geofencing
                clockInGeoRequiredField={clockInGeoRequired}
                imperialUnitsField={imperialUnits}
                clockInMaxDistanceField={clockInMaxDistance}
              />
            </OverlayGuard>

            <OverlayGuard
              hasAccess={Boolean(clockInEnabled.value && clockInFromWebAndApps.value)}
              tooltipContent={tooltipContent}
              placement={Placement.right}>
              <EarlyClockIn
                clockInEarlyEnabledField={clockInEarlyEnabled}
                clockInEarlyMinutesField={clockInEarlyMinutes}
              />
            </OverlayGuard>

            <OverlayGuard
              hasAccess={Boolean(clockInEnabled.value && clockInFromWebAndApps.value)}
              tooltipContent={tooltipContent}
              placement={Placement.right}>
              <AutomaticClockOut
                clockInAutoClockoutEnabledField={clockInAutoClockoutEnabled}
                clockInAutoClockoutMinutesField={clockInAutoClockoutMinutes}
              />
            </OverlayGuard>
          </ModalContent>

          <FooterRow mTop={20}>
            <ButtonContainer pRight={10}>
              <Button
                buttonType={ButtonType.default}
                onClick={() => {
                  reset();
                  close();
                }}>
                {gettext('Cancel')}
              </Button>
            </ButtonContainer>

            <ButtonContainer>
              <ProgressButton
                disabled={!isValid}
                buttonType={ButtonType.primary}
                onClick={submitHandler}
                height={INPUT_HEIGHT}>
                {gettext('Save')}
              </ProgressButton>
            </ButtonContainer>
          </FooterRow>
        </Container>
        <ModalErrorDisplay error={submitErrors?.length ? submitErrors[0].message : ''} />
      </Modal>
    );
  }
);

TimeClockModal.displayName = 'TimeClockModal';

export default TimeClockModal;
